package com.elan.edubridge.shared.domain.usecase

import com.elan.edubridge.shared.domain.model.AppSection
import com.elan.edubridge.shared.domain.model.SectionItem
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.UserRole
import com.elan.edubridge.shared.domain.policy.MembershipPolicy
import com.elan.edubridge.shared.domain.repository.EduConnectRepository

/**
 * 선택된 [AppSection]이 어느 조회를 호출하고 그 결과의 어떤 필드가 카드가 되는지를 결정한다.
 *
 * 이 라우팅은 Android·iOS·웹에 같은 절차로 세 번 복제돼 있었고 문구가 서로 어긋나기 시작했다.
 * 화면이 아니라 domain의 책임이므로 여기로 모은다.
 *
 * **호출부 시그니처는 유지한다** — `academyId`를 인자로 받지 않고 [SessionState.activeAcademyId]에서
 * 도출한다. Android·iOS가 이 단계에서 무수정으로 컴파일되어야 한다(§S1 리스크/완화).
 */
class GetSectionItemsUseCase(
    private val repository: EduConnectRepository,
) {
    suspend operator fun invoke(section: AppSection, session: SessionState): List<SectionItem> {
        when (section) {
            AppSection.HOME -> return listOf(homeItem(session))

            AppSection.PLATFORM -> return listOf(platformItem())

            AppSection.USERS -> return UserRole.entries.map {
                SectionItem(title = it.displayName, details = listOf("계정 역할 및 권한 관리"))
            }

            AppSection.ACADEMIES -> return repository
                .academies(session.usableMemberships.map { it.academyId })
                .map {
                    SectionItem(
                        title = it.name,
                        details = listOf(
                            it.description,
                            it.address,
                            "학생 ${it.studentCount}명 · 강사 ${it.teacherCount}명",
                        ),
                    )
                }

            else -> Unit
        }

        // 학원이 정해지지 않은 계정(가입 직후·초대 대기)은 깨진 화면이 아니라 정의된 빈 상태를 본다.
        val academyId = session.activeAcademyId ?: return listOf(noAcademyItem())
        val userId = session.account?.id.orEmpty()

        return when (section) {
            AppSection.CLASSES -> repository.classes(academyId).map {
                SectionItem(
                    title = it.subject,
                    details = listOf(
                        "담당 ${it.teacher}",
                        it.schedule.displayLabel,
                        "${it.className} · ${it.studentCount}명",
                    ),
                )
            }

            // 개인정보 격리(§4.6): 관찰자에게는 학원 전체 명부가 아니라 ACTIVE 링크 자녀만 보인다.
            AppSection.STUDENTS -> if (MembershipPolicy.isStaff(session)) {
                repository.students(academyId).map { student ->
                    SectionItem(
                        title = student.name,
                        details = listOf(
                            student.grade,
                            student.phone.orEmpty(),
                            student.guardianContacts.joinToString {
                                "${it.relation} ${it.name} ${it.phone}"
                            },
                        ),
                    )
                }
            } else {
                repository.guardianLinks(userId)
                    .filter { it.isObservable && it.academyId == academyId }
                    .map { SectionItem(title = it.studentName, details = listOf("관계: ${it.relation}")) }
            }

            // 개인정보 격리(§4.6): VIEWER는 학원 원장이 아니라 본인 미러만 본다.
            AppSection.ATTENDANCE -> if (MembershipPolicy.isStaff(session)) {
                repository.dailyRoster(academyId, serviceDate = null, classId = null).map {
                    SectionItem(
                        title = it.studentName,
                        details = listOf(it.className, it.serviceDate, it.status.displayName),
                    )
                }
            } else {
                repository.myTimeline(
                    userId = userId,
                    academyId = academyId,
                    subjectStudentId = null,
                    limit = TIMELINE_LIMIT,
                ).map {
                    SectionItem(
                        title = it.subjectStudentName,
                        details = listOf(
                            it.className ?: it.academyName,
                            it.serviceDate,
                            it.status.displayName,
                        ),
                    )
                }
            }

            AppSection.ASSIGNMENTS -> repository.studentAssignments(academyId, userId).map {
                SectionItem(
                    title = it.title,
                    details = listOf(
                        "담당 ${it.teacher}",
                        "마감 ${it.dueDate}",
                        if (it.completed) "완료" else "진행 중",
                    ),
                )
            }

            AppSection.NOTICES -> repository.notices(academyId).map {
                SectionItem(title = it.title, details = listOf(it.content, it.date))
            }

            AppSection.HOME,
            AppSection.ACADEMIES,
            AppSection.USERS,
            AppSection.PLATFORM,
            -> emptyList()
        }
    }

    private fun homeItem(session: SessionState): SectionItem {
        val menu = MembershipPolicy.sectionsFor(session).joinToString { it.displayName }
        return SectionItem(
            title = "${session.account?.name.orEmpty()}님, 안녕하세요",
            details = listOf(
                "현재 역할: ${session.activeRole.displayName}",
                "사용 가능한 메뉴: $menu",
            ),
        )
    }

    private fun platformItem(): SectionItem = SectionItem(
        title = "전체 플랫폼 관리",
        details = listOf("학원 및 사용자 현황", "서비스 운영 상태", "권한 정책 관리"),
    )

    private fun noAcademyItem(): SectionItem = SectionItem(
        title = "아직 연결된 학원이 없어요",
        details = listOf(
            "학원 초대를 받으면 여기에 표시됩니다.",
            "학원을 직접 운영한다면 학원 개설을 신청해 주세요.",
        ),
    )

    private companion object {
        const val TIMELINE_LIMIT = 30
    }
}
